import {
  useGetAgentsEstimateStats,
  useGetReceiverEstimateStats,
} from '@/hooks/useStats';
import StyledContainer from '@/components/styled/StyledContainer';
import StyledListHeader from '@/components/styled/StyledListHeader';
import StyledNextPreviousButtons from '@/components/styled/StyledNextPreviousButtons';
import StyledSelect from '@/components/styled/StyledSelect';
import StyledTable from '@/components/styled/StyledTable';
import { PAGE_SIZE } from '@/constants/pageSize';
import useTableControls from '@/hooks/useTableControls';
import { LabelValue } from '@/types/global';
import { eachMonthOfInterval, format } from 'date-fns';
import { useMemo, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { RECEIVER_ESTIMATE_STATS_COLUMNS } from '@/types/stats/ReceiverEstimateStatsColumnDef';
import { AGENTS_ESTIMATE_STATS_COLUMNS } from '@/types/stats/AgentsEstimateStatsColumnDef';
import { Loader } from 'lucide-react';
import { useGetMe } from '@/api/user.api';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { TabsContent } from '@radix-ui/react-tabs';
import { useTranslation } from '@/i18n';
import { useTranslatedConst } from '@/hooks/useTranslatedConst';
import { useLanguage } from '@/contexts/LanguageContext';
import { LOCALES_MAP } from '@/constants/localeMap';

type FiltersType = {
  receiver?: LabelValue<string>;
  month?: LabelValue<string>;
};

type tabOptions = 'Receivers' | 'Agents';

const DistributorStatsPage = () => {
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const { control } = useForm<FiltersType>({
    mode: 'onChange',
    defaultValues: {
      receiver: undefined,
      month: undefined,
    },
  });

  const selectedReceiver = useWatch({ control, name: 'receiver' });
  const selectedMonth = useWatch({ control, name: 'month' });

  const filters = useMemo(
    () => ({
      receiverId: selectedReceiver?.value ?? undefined,
      month: selectedMonth?.value ?? undefined,
    }),
    [selectedReceiver?.value, selectedMonth?.value],
  );

  const { page, setPage, sorting, setSorting, sortField, sortOrder } =
    useTableControls();

  const { data: ReceiverEstimateStatsResponse } = useGetReceiverEstimateStats({
    page,
    pageSize: PAGE_SIZE,
    sortField,
    sortOrder,
    filters,
  });

  const monthOptions = useMemo(
    () =>
      eachMonthOfInterval({ start: new Date(2025, 0, 1), end: new Date() }).map(
        (month) => ({
          label: format(month, 'LLLL yyyy', {
            locale: LOCALES_MAP[currentLanguage],
          }),
          value: format(month, 'yyyy-MM'),
        }),
      ),
    [currentLanguage],
  );

  const receiversOptions = useMemo(
    () =>
      ReceiverEstimateStatsResponse?.receivers.map(({ id, name }) => ({
        label: name,
        value: id,
      })),
    [ReceiverEstimateStatsResponse?.receivers],
  );

  const { data: userInfo } = useGetMe();

  const isAdmin = useMemo(() => userInfo?.isAdmin ?? false, [userInfo]);

  const [currTab, setCurrTab] = useState<tabOptions>('Receivers');

  return (
    <StyledContainer>
      <StyledListHeader
        title={t('stats.title')}
        subTitle={t('stats.subTitle')}
      />
      <Tabs
        className="p-6"
        value={currTab}
        onValueChange={(value) => {
          setCurrTab(value as tabOptions);
        }}
      >
        {isAdmin && (
          <div className="flex mb-6 w-full justify-between">
            <TabsList>
              <TabsTrigger value="Receivers" className="hover:cursor-pointer">
                {t('stats.receivers')}
              </TabsTrigger>
              <TabsTrigger value="Agents" className="hover:cursor-pointer">
                {t('stats.agents')}
              </TabsTrigger>
            </TabsList>
          </div>
        )}
        <div className="flex flex-col w-full h-auto gap-6">
          <TabsContent value="Receivers">
            <div className="flex flex-col gap-2">
              <span className="text-xs font-normal text-gray-600">
                {t('stats.filterBy')}
              </span>
              <div className="flex flex-col md:flex-row justify-between gap-4">
                <div className="flex flex-col md:flex-row gap-4">
                  <StyledSelect
                    className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                    control={control}
                    options={receiversOptions}
                    name="receiver"
                    placeholder={t('stats.receiverPlaceholder')}
                    isClearable={true}
                  />
                  <StyledSelect
                    className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                    control={control}
                    options={monthOptions}
                    name="month"
                    placeholder={t('stats.month')}
                    isClearable={true}
                  />
                </div>
              </div>
            </div>
            <StyledTable
              columns={getTranslatedConst(RECEIVER_ESTIMATE_STATS_COLUMNS)}
              data={ReceiverEstimateStatsResponse?.receivers ?? []}
              getRowId={(row) => row.id}
              sorting={sorting}
              setSorting={setSorting}
            />
            <StyledNextPreviousButtons
              dataLength={ReceiverEstimateStatsResponse?.total ?? 0}
              page={page}
              pageSize={PAGE_SIZE}
              setPage={setPage}
              label={t('stats.distributor')}
            />
          </TabsContent>
          <TabsContent value="Agents">
            <DistributorAgentsTable />
          </TabsContent>
        </div>
      </Tabs>
    </StyledContainer>
  );
};

export default DistributorStatsPage;

const DistributorAgentsTable = () => {
  const { currentLanguage } = useLanguage();
  const { t } = useTranslation();
  const getTranslatedConst = useTranslatedConst();
  const { control } = useForm<FiltersType>({
    mode: 'onChange',
    defaultValues: {
      month: undefined,
    },
  });

  const selectedMonth = useWatch({ control, name: 'month' });

  const filters = useMemo(
    () => ({
      month: selectedMonth?.value ?? undefined,
    }),
    [selectedMonth?.value],
  );

  const { page, setPage, sorting, setSorting, sortField, sortOrder } =
    useTableControls();

  const {
    data: AgentsEstimateStatsResponse,
    isLoading,
    isSuccess,
  } = useGetAgentsEstimateStats({
    page,
    pageSize: PAGE_SIZE,
    sortField,
    sortOrder,
    filters,
  });

  const monthOptions = useMemo(
    () =>
      eachMonthOfInterval({ start: new Date(2025, 0, 1), end: new Date() }).map(
        (month) => ({
          label: format(month, 'LLLL yyyy', {
            locale: LOCALES_MAP[currentLanguage],
          }),
          value: format(month, 'yyyy-MM'),
        }),
      ),
    [currentLanguage],
  );

  return isLoading ? (
    <Loader />
  ) : (
    isSuccess && (
      <>
        <div className="flex flex-col gap-2">
          <span className="text-xs font-normal text-gray-600">
            {t('stats.filterBy')}
          </span>
          <div className="flex flex-col md:flex-row justify-between gap-4">
            <div className="flex flex-col md:flex-row gap-4">
              <StyledSelect
                className="min-w-1/3 md:min-w-48 lg:min-w-60 xl:min-w-80"
                control={control}
                options={monthOptions}
                name="month"
                placeholder={t('stats.month')}
                isClearable={true}
              />
            </div>
          </div>
        </div>
        <StyledTable
          columns={getTranslatedConst(AGENTS_ESTIMATE_STATS_COLUMNS)}
          data={AgentsEstimateStatsResponse?.agents ?? []}
          getRowId={(row) => row.id}
          sorting={sorting}
          setSorting={setSorting}
        />
        <StyledNextPreviousButtons
          dataLength={AgentsEstimateStatsResponse?.total ?? 0}
          page={page}
          pageSize={PAGE_SIZE}
          setPage={setPage}
          label={t('stats.agent')}
        />
      </>
    )
  );
};
